<template>
  <div class="page-container">
    <!-- Page Header -->
    <page-header
      icon="mdi-silverware-fork-knife"
      :title="$t('restaurants')"
      subtitle="Pregledajte dostupne restorane"
      icon-color="primary"
    >
    </page-header>

    <!-- Toolbar -->
    <page-toolbar v-if="!device.isMobile" :searchable="true" :search.sync="search">
      <template #filters>
        <!-- Add any filters here if needed -->
      </template>
    </page-toolbar>

    <!-- Mobile Filters -->
    <template v-else>
      <mobile-filters
        :filters="mobileFilterConfig"
        @update:filter="handleMobileFilterUpdate"
        @apply="onFiltersApply"
        @reset="resetFilters"
      />
    </template>

    <!-- Content Area -->
    <div :class="device.isMobile ? 'px-0 mt-2' : 'px-6 mt-2'">
      <v-data-table
        v-if="!device.isMobile"
        :headers="headers"
        :items="restaurantsWithFakeData"
        :search="search"
        :items-per-page="10"
        class="ordera-box-shadow striped-table"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <v-icon left color="primary" size="20">mdi-map-marker</v-icon>
            {{ item.name }}
          </div>
        </template>

        <template v-slot:item.shifts="{ item }">
          <v-chip-group>
            <v-chip
              v-for="(shift, index) in item.shifts"
              :key="index"
              color="orange"
              dark
            >
              {{ shift }}
            </v-chip>
          </v-chip-group>
        </template>

        <template v-slot:item.menu_type="{ item }">
          <v-chip small :color="item.menu_type_color" text-color="white">
            {{ item.menu_type }}
          </v-chip>
        </template>

        <template v-slot:item.uses_credit="{ item }">
          <v-icon :color="item.uses_credit ? 'success' : 'error'">
            {{ item.uses_credit ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.shows_price="{ item }">
          <v-icon :color="item.shows_price ? 'success' : 'error'">
            {{ item.shows_price ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.limit_type="{ item }">
          <v-chip small :color="item.limit_type_color" text-color="white">
            {{ item.limit_type }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-information"
            color="primary"
            :tooltip="$t('contact_info')"
            @click="openContactDialog(item)"
          />
        </template>
      </v-data-table>

      <!-- Mobile: Cards -->
      <mobile-data-cards
        v-else
        :headers="mobileHeaders"
        :items="restaurantsWithFakeData"
        :fields-per-row="1"
        :bold-fields="['name']"
        @click:row="viewRestaurant"
      >
        <template v-slot:item.name="{ item }">
          <div class="font-weight-medium">{{ item.name }}</div>
        </template>

        <template v-slot:item.shifts="{ item }">
          {{ item.shifts.join(", ") }}
        </template>

        <template v-slot:item.menu_type="{ item }">
          <v-chip x-small :color="item.menu_type_color" text-color="white">
            {{ item.menu_type }}
          </v-chip>
        </template>

        <template v-slot:item.uses_credit="{ item }">
          <v-icon small :color="item.uses_credit ? 'success' : 'error'">
            {{ item.uses_credit ? "mdi-check-circle" : "mdi-close-circle" }}
          </v-icon>
        </template>

        <template v-slot:item.limit_type="{ item }">
          <v-chip x-small :color="item.limit_type_color" text-color="white">
            {{ item.limit_type }}
          </v-chip>
        </template>
      </mobile-data-cards>
    </div>

    <!-- Restaurant Contact Dialog -->
    <restaurant-contact-dialog v-model="contactDialog" :restaurant="selectedRestaurant" />
  </div>
</template>

<script>
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileFilters from "@/components/global/MobileFilters.vue";
import hasMobileFilters from "@/mixins/hasMobileFilters";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import RestaurantContactDialog from "@/components/customer/restaurants/RestaurantContactDialog.vue";

export default {
  name: "CustomerRestaurantsPage",
  mixins: [hasMobileFilters],
  components: {
    PageHeader,
    PageToolbar,
    MobileFilters,
    MobileDataCards,
    RestaurantContactDialog,
  },
  data() {
    return {
      search: "",
      contactDialog: false,
      selectedRestaurant: null,
    };
  },
  computed: {
    device() {
      return this.$store.state.device;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    restaurantsWithFakeData() {
      const shiftOptions = [[1], [1, 2], [1, 2, 3], [2, 3], [1, 2, 3, 4]];

      const menuTypes = [
        { name: "Тип 1", color: "blue" },
        { name: "Тип 2", color: "green" },
      ];

      const limitTypes = [
        { name: "Број оброка", color: "indigo" },
        { name: "Цијена", color: "teal" },
        { name: "Без лимита", color: "grey" },
      ];

      return this.restaurants
        .map((restaurant, index) => {
          const menuType = menuTypes[index % menuTypes.length];
          const limitType = limitTypes[index % limitTypes.length];

          return {
            ...restaurant,
            shifts: shiftOptions[index % shiftOptions.length],
            menu_type: menuType.name,
            menu_type_color: menuType.color,
            uses_credit: Math.random() > 0.3, // 70% chance true
            shows_price: Math.random() > 0.4, // 60% chance true
            limit_type: limitType.name,
            limit_type_color: limitType.color,
          };
        })
        .filter((restaurant) => {
          if (!this.search) return true;
          const searchLower = this.search.toLowerCase();
          return (
            restaurant.name.toLowerCase().includes(searchLower) ||
            restaurant.menu_type?.toLowerCase().includes(searchLower) ||
            restaurant.limit_type?.toLowerCase().includes(searchLower)
          );
        });
    },
    headers() {
      return [
        { text: "ID", value: "id", sortable: false, width: "60px" },
        { text: this.$t("name"), value: "name", sortable: true },
        { text: "Смјене", value: "shifts", sortable: false },
        { text: "Тип менија", value: "menu_type", sortable: false, align: "center" },
        { text: "Кредити", value: "uses_credit", sortable: false, align: "center" },
        { text: "Цијена", value: "shows_price", sortable: false, align: "center" },
        { text: "Тип лимита", value: "limit_type", sortable: false, align: "center" },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          width: "80px",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: "Смјене", value: "shifts" },
        { text: "Тип менија", value: "menu_type" },
        { text: "Кредити", value: "uses_credit" },
        { text: "Тип лимита", value: "limit_type" },
      ];
    },
    mobileFilterDefs() {
      return {
        search: {
          type: "text",
          model: "search",
          label: this.$t("search"),
          icon: "mdi-magnify",
        },
      };
    },
  },
  methods: {
    onFiltersApply() {
      // Apply filters
    },
    resetFilters() {
      this.search = "";
    },
    viewRestaurant(restaurant) {
      // Navigate to restaurant details or menu
      // TODO: Navigate to restaurant menu page
    },
    openContactDialog(restaurant) {
      this.selectedRestaurant = restaurant;
      this.contactDialog = true;
    },
  },
};
</script>
